import { AbstractControl, FormGroup } from '@angular/forms';
import { UnitsGridColumn } from './units-grid-input.models';

interface FocusTarget {
  rowIndex: number;
  columnIndex: number;
}

export interface UnitsGridFocusCoordinatorConfig {
  hostElement: HTMLElement;
  getColumns: () => readonly UnitsGridColumn[];
  getRows: () => FormGroup[];
  isNumericColumn: (column: UnitsGridColumn) => boolean;
  isEditableColumn: (column: UnitsGridColumn) => boolean;
  getControl: (row: FormGroup, fieldName: string) => AbstractControl | null;
  isVirtualScrollEnabled: () => boolean;
  scrollToVirtualIndex: (rowIndex: number) => void;
}

export class UnitsGridFocusCoordinator {
  constructor(private readonly config: UnitsGridFocusCoordinatorConfig) {}

  public handleTab(rowIndex: number, columnIndex: number, shiftKey: boolean): void {
    const activeInput = this.config.hostElement.querySelector(
      'input.p-inputnumber-input:focus'
    ) as HTMLInputElement | null;
    activeInput?.blur();

    setTimeout(() => {
      const nextTarget = this.findAdjacentEditableCell(rowIndex, columnIndex, shiftKey ? -1 : 1);
      if (nextTarget) {
        this.focusEditableCell(nextTarget.rowIndex, nextTarget.columnIndex);
      }
    }, 0);
  }

  public handleEnter(rowIndex: number, columnIndex: number, shiftKey: boolean): void {
    const activeInput = this.config.hostElement.querySelector(
      'input.p-inputnumber-input:focus'
    ) as HTMLInputElement | null;
    activeInput?.blur();

    setTimeout(() => {
      const nextTarget = this.findEditableCellInColumn(rowIndex, columnIndex, shiftKey ? -1 : 1);
      if (nextTarget) {
        this.focusEditableCell(nextTarget.rowIndex, nextTarget.columnIndex);
      }
    }, 0);
  }

  private findAdjacentEditableCell(
    rowIndex: number,
    columnIndex: number,
    direction: 1 | -1
  ): FocusTarget | null {
    const columns = this.config.getColumns();
    const rows = this.config.getRows();
    const rowCount = rows.length;
    if (columns.length === 0 || rowCount === 0) {
      return null;
    }

    const isLastRow = rowIndex === rowCount - 1;

    // When tabbing forward on the last row (the input row), loop back to the first
    // editable column of that input row instead of wrapping all the way to row 0.
    if (isLastRow && direction === 1) {
      const isPastLastColumn = columnIndex >= columns.length - 1 ||
        !this.hasMoreEditableColumnsInRow(rowIndex, columnIndex, columns, rows);

      if (isPastLastColumn) {
        const firstTarget = this.findFirstEditableColumnInRow(rowIndex, columns, rows);
        if (firstTarget) {
          return firstTarget;
        }
      }
    }

    const totalSlots = rowCount * columns.length;
    let index = rowIndex * columns.length + columnIndex;

    for (let step = 0; step < totalSlots; step++) {
      index = (index + direction + totalSlots) % totalSlots;

      const targetRowIndex = Math.floor(index / columns.length);
      const targetColumnIndex = index % columns.length;
      const targetColumn = columns[targetColumnIndex];

      if (!targetColumn || !this.config.isNumericColumn(targetColumn) || !this.config.isEditableColumn(targetColumn)) {
        continue;
      }

      const row = rows.at(targetRowIndex);
      const control = row ? this.config.getControl(row, targetColumn.field) : null;
      if (!control || control.disabled) {
        continue;
      }

      return { rowIndex: targetRowIndex, columnIndex: targetColumnIndex };
    }

    return null;
  }

  private hasMoreEditableColumnsInRow(
    rowIndex: number,
    columnIndex: number,
    columns: readonly UnitsGridColumn[],
    rows: FormGroup[]
  ): boolean {
    const row = rows.at(rowIndex);
    if (!row) {
      return false;
    }

    for (let c = columnIndex + 1; c < columns.length; c++) {
      const col = columns[c];
      if (!col || !this.config.isNumericColumn(col) || !this.config.isEditableColumn(col)) {
        continue;
      }
      const control = this.config.getControl(row, col.field);
      if (control && !control.disabled) {
        return true;
      }
    }

    return false;
  }

  private findFirstEditableColumnInRow(
    rowIndex: number,
    columns: readonly UnitsGridColumn[],
    rows: FormGroup[]
  ): FocusTarget | null {
    const row = rows.at(rowIndex);
    if (!row) {
      return null;
    }

    for (let c = 0; c < columns.length; c++) {
      const col = columns[c];
      if (!col || !this.config.isNumericColumn(col) || !this.config.isEditableColumn(col)) {
        continue;
      }
      const control = this.config.getControl(row, col.field);
      if (control && !control.disabled) {
        return { rowIndex, columnIndex: c };
      }
    }

    return null;
  }

  private findEditableCellInColumn(
    rowIndex: number,
    columnIndex: number,
    direction: 1 | -1
  ): FocusTarget | null {
    const columns = this.config.getColumns();
    const rows = this.config.getRows();
    const rowCount = rows.length;
    const column = columns[columnIndex];

    if (!column || rowCount === 0) {
      return null;
    }

    for (
      let targetRowIndex = rowIndex + direction;
      targetRowIndex >= 0 && targetRowIndex < rowCount;
      targetRowIndex += direction
    ) {
      if (!this.config.isNumericColumn(column) || !this.config.isEditableColumn(column)) {
        return null;
      }

      const row = rows.at(targetRowIndex);
      const control = row ? this.config.getControl(row, column.field) : null;
      if (!control || control.disabled) {
        continue;
      }

      return { rowIndex: targetRowIndex, columnIndex };
    }

    return null;
  }

  private focusEditableCell(rowIndex: number, columnIndex: number): void {
    const selector = `td[data-grid-row="${rowIndex}"][data-grid-col="${columnIndex}"]`;
    const visibleCell = this.config.hostElement.querySelector(selector) as HTMLElement | null;
    if (visibleCell) {
      this.focusCell(visibleCell);
      return;
    }

    if (this.config.isVirtualScrollEnabled()) {
      this.config.scrollToVirtualIndex(rowIndex);
    }

    this.focusEditableCellInDom(rowIndex, columnIndex, 0);
  }

  private focusEditableCellInDom(rowIndex: number, columnIndex: number, attempt: number): void {
    const selector = `td[data-grid-row="${rowIndex}"][data-grid-col="${columnIndex}"]`;
    const cell = this.config.hostElement.querySelector(selector) as HTMLElement | null;
    if (!cell) {
      if (attempt < 6) {
        setTimeout(() => this.focusEditableCellInDom(rowIndex, columnIndex, attempt + 1), 0);
      }

      return;
    }

    this.focusCell(cell);
  }

  private focusCell(cell: HTMLElement): void {
    cell.click();
    this.focusCellInput(cell, 0);
  }

  private focusCellInput(cell: HTMLElement, attempt: number): void {
    const input = cell.querySelector('input.p-inputnumber-input') as HTMLInputElement | null;
    if (input) {
      input.focus();
      input.select();
      return;
    }

    if (attempt < 6) {
      setTimeout(() => this.focusCellInput(cell, attempt + 1), 0);
    }
  }
}
